/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of OMGH5ClickEvent
 */

import { IPlanObject } from 'com/didichuxing/IPlanObject';
import { IObjectMap } from 'com/didichuxing/IObjectMap';
import { IContext } from 'com/didichuxing/context/IContext';
import { IAttrs } from 'com/didichuxing/tracker/IAttrs';
import { IAutoClickCover } from 'com/didichuxing/tracker/IAutoClickCover';
import { IClickFilter } from 'com/didichuxing/tracker/IClickFilter';
import { IElementExtraAttrs } from 'com/didichuxing/tracker/h5/plugins/utils/IElementExtraAttrs';
import { IViewportExtraAttrs } from 'com/didichuxing/tracker/h5/plugins/utils/IViewportExtraAttrs';
import ElementUtils from 'com/didichuxing/tracker/h5/plugins/utils/ElementUtils';
import SequencedEvent from 'com/didichuxing/tracker/plugin/events/SequencedEvent';
import PluginUtils from 'com/didichuxing/tracker/plugin/utils/PluginUtils';

export interface ITargetPosition {
    readonly clientX: number;
    readonly clientY: number;
    readonly pageX: number;
    readonly pageY: number;
}

interface IEventInfo {

    /**
     * 目标点击元素对象。
     */
    readonly target: Element;

    /**
     * @readonly
     * 点击的页面元素坐标。
     */
    readonly targetPosition: ITargetPosition;

    /**
     * 元素自己点击属性设置对象。
     * 如果设置了此参数，那么会在这些元素上应用设置在元素节点属性值作为监控数据发送。
     * 设置attr属性，比如{'attr': 'auto-track-click'}如果设置了此属性，那么会自动收集此属性对应的值。
     * 值必须为JSON数据的stringify形式，统计代码会解析json中的e（非必须，根据业务需要设置）字段和attrs（非必须）字段，
     * 自动覆盖默认的OMGH5Click事件标记及自动扩展原有attrs字段。
     */
    clickedCover?: IAutoClickCover;
    /**
     * 是否去遍历当前节点中 clickedCover 的 子元素
     */
    clickedCoverisChild?: boolean;

    /**
     * 只监控符合filters规则的元素
     * 如果设置了此参数，那么会在这些元素上应用设置在元素节点属性值作为监控数据发送。
     * 1.设置一个class，比如{'class': 'order-click'}，则只会发送那些有class为'order-click'的元素的点击数据。
     * 2.设置一组class，比如{'class': ['order-click', 'cancel-click']}。
     * 3.设置attr属性，与enableClickAutoTrack中使用attr方式一致。
     */
    clickedFilter?: IClickFilter;
}

interface ICustomClickedInfo {

    /**
     * 自定义事件名
     */
    readonly e?: string;

    /**
     * 自定义attrs
     */
    readonly attrs?: IPlanObject;
}

interface IClickEventAttrs extends IElementExtraAttrs {

    /**
     * @readonly
     * 点击点的X坐标
     */
    readonly pointX: number;

    /**
     * @readonly
     * 点击点的Y坐标
     */
    readonly pointY: number;

}

export default class OMGH5ClickEvent extends SequencedEvent {

    /**
     * @readonly
     * 事件作用节点对象。
     */
    protected readonly target: HTMLElement;

    /**
     * @readonly
     * 事件信息。
     */
    protected readonly eventInfo: IEventInfo;

    /**
     * @readonly
     * 自定义自动点击信息。
     */
    protected readonly customClickedInfo: ICustomClickedInfo | undefined;

    /**
     * @readonly
     * 自定义过滤自动点击信息。
     */
    protected readonly customFilterClickedInfo: ICustomClickedInfo | undefined;

    /**
     * @constructor
     * @param {IAttrs} attrs
     * @param {IContext} context
     */
    constructor(attrs: IAttrs, context: IContext, eventInfo: IEventInfo) {
        super(attrs, context);
        this.target = eventInfo.target as HTMLElement;
        this.eventInfo = eventInfo;
        if (eventInfo.clickedCover
        // && this.target.hasAttribute(eventInfo.clickedCover.attr)
        ) {
            if (eventInfo.clickedCover.attr) {
                const target = this.getRealTarget(this.target, eventInfo.clickedCover.attr);
                if (target) {
                    this.target = target;
                }
                this.customClickedInfo = JSON.parse(
                    this.target.getAttribute(eventInfo.clickedCover.attr) || '{}'
                ) as ICustomClickedInfo;
            }
        }
        if (eventInfo.clickedCover && eventInfo.clickedCoverisChild) {
            this.getTargetChildAttrs(this.target, eventInfo.clickedCover.attr);
        }
        if (eventInfo.clickedFilter) {
            if (eventInfo.clickedFilter.attr && this.target.hasAttribute(eventInfo.clickedFilter.attr)) {
                this.customFilterClickedInfo = JSON.parse(
                    this.target.getAttribute(eventInfo.clickedFilter.attr) || '{}'
                ) as ICustomClickedInfo;
            }
        }
    }

    private getRealTarget(target: HTMLElement, attrKey: string): HTMLElement | null {
        if (!attrKey) {
            return target;
        }
        while (target && target !== document.body && target !== document.documentElement) {
            if (target.hasAttribute(attrKey)) {
                return target;
            }
            target = target.parentNode as any;
        }
        return null;
    }

    /**
     * 获取子节点中的信息
     * @param target
     * @param attr
     */
    private getTargetChildAttrs(target: Element, attr: string) {
        const childArr = target.children;
        for (let i = 0, len = childArr.length; i < len; i++) {
            if (childArr[i].nodeType === 1) { // nodeType==1 的节点为元素节点。
                const childNodeAttrs = JSON.parse(
                    childArr[i].getAttribute(attr) || '{}'
                ) as ICustomClickedInfo;
                Object.assign(this.customClickedInfo || {}, childNodeAttrs);
                this.getTargetChildAttrs(childArr[i], attr);
            }
        }
    }

    /**
     * @override
     * @inheritDoc
     * @returns {string}
     */
    public getType(): string {
        if (this.customFilterClickedInfo && this.customFilterClickedInfo.e) {
            return this.customFilterClickedInfo.e;
        }
        if (this.customClickedInfo && this.customClickedInfo.e) {
            return this.customClickedInfo.e;
        }
        return PluginUtils.EVENT_ID_CLICK;
    }

    /**
     * 获取点击事件额外上报的信息数据。
     * @returns {IClickedAttrs}
     */
    private get clickedExtraAttrs(): IClickEventAttrs | IViewportExtraAttrs {
        const targetPostion: ITargetPosition = this.eventInfo.targetPosition;
        return {
            ...ElementUtils.getElementExtraAttrs(this.target),
            pointX: targetPostion.pageX
                || (targetPostion.clientX + (document.documentElement.scrollLeft || document.body.scrollLeft || 0)),
            pointY: targetPostion.pageY
                || (targetPostion.clientY + (document.documentElement.scrollTop || document.body.scrollTop || 0))
        };
    }

    /**
     * 检查属性
     */
    private get clickedAttrs(): IAttrs {
        let attrs = super.getAttrs() || {};
        if (this.customClickedInfo && this.customClickedInfo.attrs) {
            Object.assign(attrs, this.customClickedInfo.attrs);
        }
        if (this.customFilterClickedInfo && this.customFilterClickedInfo.attrs) {
            attrs = this.customFilterClickedInfo.attrs;
        }
        return attrs;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IAttrs}
     */
    protected getAttrs(): IAttrs {
        return {
            ['OMGH5CLICK']: 1,
            ...this.clickedExtraAttrs,
            ...this.clickedAttrs
            // ...super.getAttrs()
        };
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IObjectMap<string>}
     */
    protected getAttrsKeyMap(): IObjectMap<string> {
        return {
            ...super.getAttrsKeyMap(),
            ...ElementUtils.getElementExtraAttrsMap(),
            pointX: 'x',
            pointY: 'y'
        };
    }
}
